export const TermsData = {
  title: 'Terms of Service',
  lastUpdated: 'Last Updated: October 15, 2025',
  importantNotice: 'IMPORTANT: PLEASE READ THESE TERMS CAREFULLY. By accessing or using the Nobilis platform (the "Platform"), you agree to be bound by these Terms of Service ("Terms"). These Terms contain important information about your legal rights, including mandatory arbitration and class action waiver provisions in Section 17, which affect your ability to bring legal claims against Nobilis. If you do not agree to these Terms, you may not access or use the Platform.',
  sections: [
    {
      title: '1. AGREEMENT TO TERMS',
      paragraphs: [
        'These Terms constitute a legally binding agreement between you ("you," "your," or "Member") and Nobilis Global, LLC ("Nobilis," "we," "us," or "our") governing your access to and use of the Platform. By creating an account, accessing the Platform, or using any of our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy, which is incorporated herein by reference.'
      ]
    },
    {
      title: '2. PLATFORM OVERVIEW',
      paragraphs: [
        'Nobilis is an exclusive social network designed for accomplished individuals across various fields, including but not limited to business leaders, athletes, scientists, artists, influencers, and other distinguished professionals. The Platform provides five primary services for Members:'
      ],
      bullets: [
        'Networking Services: Tools to connect, communicate, and build relationships with other Members.',
        'Mastermind Circles: Exclusive, invitation-only private forums where select Members collaborate on specific topics, projects, or challenges in confidential settings.',
        'Open Discussions: Platform-wide discussion boards and topic-based forums where Members can engage in broader conversations, share insights, and exchange ideas with the wider Nobilis community.',
        'Experiences: Curated events, activities, and unique opportunities hosted by Members or third-party providers (Partners).',
        'Expert Consultations: A marketplace where Members offer professional expertise, consulting services, and advisory sessions to other Members, with Nobilis facilitating connections and transactions.'
      ],
      additionalParagraphs: [
        'Platform can be accessed by the Partners, who should align with Partners criteria and sign partnership agreement and by Administrators - Nobilis employees with limited role base views internal confidential agreement and regulations.'
      ]
    },
    {
      title: '3. ELIGIBILITY',
      paragraphs: [
        'To use the Platform, you must:'
      ],
      bullets: [
        'Be at least 18 years of age;',
        'Have the legal capacity to enter into a binding contract;',
        'Not be prohibited from using the Platform under applicable laws;',
        'Meet one or more of the following membership criteria: Achievement-Based Qualification: Demonstrated significant accomplishments in your field, including but not limited to business leadership, athletics, science, arts, entertainment, or other professional achievements; OR Wealth-Based Qualification: Verified net worth of at least thirty million U.S. dollars ($30,000,000) in assets;',
        'Provide accurate and complete information during the application process; and',
        'Submit all requested documentation to verify your eligibility.',
        'Sign Partners agreement with Nobilis or Have Employment contract (freelance or permanent)'
      ],
      additionalParagraphs: [
        'Nobilis reserves the right to reject any membership application or terminate any membership at our sole discretion, with or without cause, and without prior notice.'
      ]
    },
    {
      title: '4. ACCOUNT REGISTRATION AND VERIFICATION',
      subsections: [
        {
          subtitle: '4.1 Account Creation',
          paragraphs: [
            'To access the Platform, you must create an account by providing accurate, current, and complete information, including your full legal name, email address, professional background, and other information requested during registration. You agree to maintain and promptly update your account information to keep it accurate and complete.'
          ]
        },
        {
          subtitle: '4.2 Membership Verification Requirements',
          paragraphs: [
            'Depending on your qualification pathway, you may be required to provide:',
            'For Achievement-Based Members:'
          ],
          bullets: [
            'Verified social media accounts with engagement metrics and follower counts',
            'Professional credentials, certifications, or licenses',
            'Media coverage, awards, or recognition documentation',
            'References from current Members or recognized professionals',
            'Public records of achievements (publications, patents, performances, competitions, etc.)',
            'Corporate documentation (e.g., proof of C-suite position, board membership, company ownership)'
          ],
          additionalParagraphs: [
            'For Wealth-Based Members:'
          ],
          additionalBullets: [
            'Bank statements showing account balances',
            'Investment account statements (brokerage, retirement accounts, etc.)',
            'Real estate appraisals and mortgage statements',
            'Business valuation reports',
            'Trust documents or asset schedules',
            'Verification letters from financial advisors, CPAs, or attorneys',
            'Tax returns or financial statements',
            'Other documentation demonstrating net assets of at least $30,000,000 USD'
          ]
        },
        {
          subtitle: '4.3 Verification and Due Diligence Process',
          paragraphs: [
            'Authorization. By submitting your application, you authorize Nobilis and its third-party partners to conduct comprehensive verification and due diligence, including but not limited to:'
          ],
          bullets: [
            'Review and verify all information and documentation you provide',
            'Conduct background checks, including criminal, civil, and financial records',
            'Review publicly available information, including news articles, social media, court records, and other public sources',
            'Assess reputation, public standing, and any controversies or negative publicity',
            'Contact financial institutions, employers, references, and other parties to confirm your eligibility',
            'Conduct background checks and review public records',
            'Use third-party verification services and databases'
          ],
          additionalParagraphs: [
            'Confidentiality. All verification information is treated as strictly confidential and will be handled in accordance with our Privacy Policy. Nobilis implements enhanced security measures to protect sensitive financial and personal information.',
            'Ongoing Verification. Nobilis reserves the right to request updated documentation at any time to confirm your continued eligibility. Failure to provide requested documentation may result in suspension or termination of your membership, partnership or employment.',
            'Third-Party Verifiers. We may engage reputable third-party verification services to authenticate documentation and confirm your eligibility. These services are bound by strict confidentiality agreements.',
            'Processing Time. Verification may take 7-30 business days, depending on the complexity of your application and the responsiveness of third parties.'
          ]
        },
        {
          subtitle: '4.4 Background and Reputation Review',
          paragraphs: [
            'In addition to document verification, Nobilis conducts thorough due diligence on all applicants, including:'
          ],
          bullets: [
            'Public Records Review: Search of criminal records, civil litigation history, bankruptcy filings, and regulatory actions',
            'Reputation Assessment: Review of media coverage, online presence, professional standing, and public perception',
            'Reference Checks: Verification of professional accomplishments and character through references',
            'Risk Assessment: Evaluation of any potential reputational, legal, or security risks to the Nobilis community'
          ],
          additionalParagraphs: [
            'This due diligence process may take 7-30 business days or longer for complex cases. Nobilis reserves the right to deny membership based on findings from this process, even if eligibility criteria are met.'
          ]
        },
        {
          subtitle: '4.5 Accuracy of Information',
          paragraphs: [
            'You represent and warrant that:'
          ],
          bullets: [
            'All information and documentation you provide is true, accurate, complete, and not misleading',
            'You are the rightful owner of all assets claimed',
            'You have the legal authority to provide all documentation submitted',
            'All documentation is authentic and has not been altered or falsified'
          ],
          additionalParagraphs: [
            'False or misleading information constitutes grounds for immediate denial or termination of membership and may result in legal action.'
          ]
        },
        {
          subtitle: '4.6 No Guarantee of Acceptance',
          paragraphs: [
            'Meeting the eligibility criteria does not guarantee acceptance into Nobilis. Nobilis maintains sole discretion over all membership decisions and may consider additional factors, including:'
          ],
          bullets: [
            'Community fit and alignment with Nobilis values',
            'Reputation and public standing',
            'Potential contribution to the community',
            'Available capacity in the network',
            'Any other factors Nobilis deems relevant'
          ]
        },
        {
          subtitle: '4.7 Account Security',
          paragraphs: [
            'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify Nobilis immediately of any unauthorized access or use of your account. Nobilis is not liable for any loss or damage arising from your failure to protect your account credentials.'
          ]
        }
      ]
    },
    {
      title: '5. MEMBERSHIP FEES AND PAYMENT',
      subsections: [
        {
          subtitle: '5.1 Subscription Fees',
          paragraphs: [
            'Initiation Fee. Access to the Platform requires payment of a one-time, non-refundable initiation fee as specified during registration. This initiation fee must be paid before your membership becomes active and is separate from ongoing subscription fees.',
            'Ongoing Membership Fees. In addition to the initiation fee, Members must pay recurring membership fees to maintain active membership status. Membership fees may be paid through:'
          ],
          bullets: [
            'Cash Payment: Direct payment via credit card, debit card, bank transfer, or other accepted payment methods; or',
            'Reward Points: Redemption of reward points earned through the Platform, subject to availability and the terms of the Nobilis Rewards Program.'
          ],
          additionalParagraphs: [
            'Non-Refundable. Both initiation fees and membership fees are non-refundable except as expressly provided in these Terms or required by applicable law. If you cancel your membership or your membership is terminated, you will not receive a refund for any fees paid, including any unused portion of your membership period.',
            'Fee Modifications. Nobilis reserves the right to modify initiation fees and membership fees at any time. For existing Members, we will provide thirty (30) days\' advance notice of any fee increases. New fee structures will apply to new Members immediately upon implementation and to renewing Members at the start of their next billing cycle after the notice period.',
            'Payment Requirement. Failure to pay the initiation fee or ongoing membership fees when due may result in suspension or termination of your account and access to the Platform.'
          ]
        },
        {
          subtitle: '5.2 Payment for Experiences and Partner Consultations',
          paragraphs: [
            'When you book an Experience, Expertise or other services through the Marketplace, you agree to pay the price displayed at the time of booking, plus any applicable service fees, taxes, or charges. Nobilis acts as a payment facilitator and may charge a service fee or commission for facilitating such transactions. All payments are processed through third-party payment processors, and you agree to their terms and conditions.'
          ]
        },
        {
          subtitle: '5.3 Automatic Renewal',
          paragraphs: [
            'Unless you cancel your membership before the end of your billing period, your membership will automatically renew for successive periods of the same duration at the then-current membership fee. You authorize Nobilis to charge your payment method for renewal fees.'
          ]
        },
        {
          subtitle: '5.4 Payment Methods',
          paragraphs: [
            'You must provide a valid payment method to maintain your membership. You authorize Nobilis and its payment processors to charge all fees to your designated payment method. If your payment method fails, Nobilis may suspend or terminate your access to the Platform.'
          ]
        },
        {
          subtitle: '5.5 Taxes',
          paragraphs: [
            'You are responsible for all taxes associated with your use of the Platform, including any sales, use, value-added, or similar taxes. If Nobilis is required to collect or pay taxes on your behalf, such amounts will be charged to you.'
          ]
        },
        {
          subtitle: '5.6 Host and Partner Consultant Earnings',
          paragraphs: [
            'If you host Experiences or provide Expertise, you will receive payment for your services minus Nobilis\'s service fee or commission, which will be clearly disclosed before you offer services. Nobilis will remit payment to you according to the payment schedule specified in your Host/Partner Agreement. You are solely responsible for reporting and paying all taxes on income earned through the Marketplace, including income tax, self-employment tax, and any other applicable taxes. Nobilis may be required to report your earnings to tax authorities and may issue tax forms (such as Form 1099 in the United States) as required by law.'
          ]
        }
      ]
    },
    {
      title: '6. USER CONDUCT AND PROHIBITED ACTIVITIES',
      paragraphs: [
        'You agree to use the Platform in accordance with these Terms and all applicable laws. You further agree not to:'
      ],
      bullets: [
        'Violate any local, state, national, or international law or regulation;',
        'Infringe upon the intellectual property rights, privacy rights, or other rights of Nobilis or any third party;',
        'Post, transmit, or share content that is unlawful, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable;',
        'Impersonate any person or entity or falsely state or misrepresent your affiliation with any person or entity;',
        'Engage in any form of harassment, intimidation, or discrimination against other Members;',
        'Take photographs, videos, or recordings of other Members without their express prior consent;',
        'Share, publish, or distribute photographs, videos, recordings, or any identifying information about other Members on any public platform, social media, or with any third parties without their express written consent;',
        'Disclose the identity, attendance, participation, or involvement of any Member in Experiences, Mastermind Circles, Expert Consultations, or any other Platform activities without their express written consent;',
        'Screenshot, record, or reproduce private communications, messages, or content shared within Mastermind Circles or private conversations and share them outside the Platform;',
        'Publicize or disclose that another Member is part of the Nobilis community without their express consent;',
        'Cancel confirmed Experiences and Expertise that you are hosting without force majeure or other circumstances beyond your reasonable control, which may result in harm to Members who have booked and damage to the Nobilis community;',
        'Use the Platform for any commercial purpose not expressly authorized by these Terms, including soliciting other Members for business outside the Platform\'s designated services;',
        'Attempt to gain unauthorized access to the Platform, other Members\' accounts, or Nobilis\'s systems or networks;',
        'Use any automated means (including bots, scrapers, or crawlers) to access the Platform or collect information without our express written permission;',
        'Interfere with or disrupt the Platform or servers or networks connected to the Platform;',
        'Upload or transmit viruses, malware, or any other malicious code;',
        'Collect or store personal information about other Members without their express consent;',
        'Use the Platform to promote or facilitate illegal activities; or',
        'Encourage or enable any other person to engage in any of the foregoing prohibited activities.'
      ],
      additionalParagraphs: [
        'Violation of this Section may result in immediate suspension or termination of your account and may subject you to civil and criminal liability.'
      ]
    },
    {
      title: '7. EXPERIENCES',
      subsections: [
        {
          subtitle: '7.1 Booking and Attendance',
          paragraphs: [
            'Members may book Experiences through the Marketplace. When you book an Experience, you enter into a direct agreement with the Host and other Guests of that Experience. Nobilis acts solely as an intermediary to facilitate connections and payments. You agree to comply with all terms, rules, and instructions provided by the Host.'
          ]
        },
        {
          subtitle: '7.2 Hosting Experiences',
          paragraphs: [
            'If you host Experiences, you represent and warrant that:'
          ],
          bullets: [
            'You have all the necessary licenses, permits, insurance, and authorizations to host the Experience;',
            'The Experience complies with all applicable laws and regulations.',
            'All descriptions and information about the Experience are accurate and not misleading;',
            'You will honor all confirmed bookings unless prevented by circumstances beyond your reasonable control; and',
            'You will provide the Experience with reasonable care and skill.'
          ],
          additionalParagraphs: [
            'You agree to indemnify and hold Nobilis harmless from any claims, damages, or losses arising from your hosting of Experiences.'
          ]
        },
        {
          subtitle: '7.3 Cancellations and Refunds',
          paragraphs: [
            'Each Host sets their own cancellation and refund policy for Experiences they offer. The Host\'s specific cancellation and refund terms will be clearly displayed at the time of booking, and you agree to be bound by those terms when you book an Experience.',
            'Nobilis does not set, control, or guarantee any cancellation or refund policies. All cancellation and refund requests must be handled directly between you and the Host. In the event of a dispute regarding cancellations or refunds, Nobilis may, at its sole discretion, offer to facilitate mediation between the parties, but Nobilis is not obligated to become involved and makes no guarantees regarding dispute outcomes.',
            'You acknowledge and agree that Nobilis is not responsible for any Host\'s cancellation or refund policies, and any disputes arising from cancellations or refunds are solely between you and the Host.',
            'Cancellation and refund policies for Experiences are set by individual Hosts and will be displayed at the time of booking. Nobilis is not responsible for issuing refunds but may facilitate refunds in accordance with the Host\'s stated policy. In the event of a dispute, Nobilis may, at its sole discretion, mediate or make a final determination regarding refunds.'
          ]
        },
        {
          subtitle: '7.4 Liability for Experiences',
          paragraphs: [
            'NOBILIS DOES NOT ENDORSE, GUARANTEE, OR ASSUME ANY RESPONSIBILITY FOR THE QUALITY, SAFETY, OR LEGALITY OF ANY EXPERIENCE. You acknowledge that participation in Experiences may involve inherent risks, and you assume all such risks voluntarily. Hosts are solely responsible for the Experiences they offer, and Nobilis shall not be liable for any injury, loss, damage, or harm arising from your participation in or hosting of any Experience.'
          ]
        }
      ]
    },
    {
      title: '8. EXPERT CONSULTATIONS',
      subsections: [
        {
          subtitle: '8.1 Marketplace for Professional Services',
          paragraphs: [
            'The Platform enables Members to offer and purchase professional expertise, consulting services, artist bookings for private or public events and advisory sessions ("Expertise"). Nobilis acts solely as a marketplace platform to facilitate connections and transactions between Members or between Members & Partners. Nobilis is not a party to any Expertise agreement between Members or Partners.'
          ]
        },
        {
          subtitle: '8.2 Offering Expertise',
          paragraphs: [
            'If you offer Expertise as a Partner or Member, you represent and warrant that:'
          ],
          bullets: [
            'You possess the necessary qualifications, expertise, licenses, and credentials to provide the services you offer.',
            'All information about your credentials, experience, and expertise is accurate, current, and not misleading;',
            'You will provide services with professional care, skill, and diligence;',
            'You will only offer services within your area of expertise and competence.',
            'You comply with all applicable professional standards, codes of conduct, and regulations governing your profession;',
            'You maintain appropriate professional liability insurance as required by your profession or jurisdiction;',
            'You will honor all confirmed bookings unless prevented by circumstances beyond your reasonable control; and',
            'You will maintain the confidentiality of any sensitive or proprietary information shared during consultations, except as required by law.'
          ],
          additionalParagraphs: [
            'You agree to indemnify and hold Nobilis harmless from any claims, damages, or losses arising from your provision of Expertise.'
          ]
        },
        {
          subtitle: '8.3 Purchasing Expertise',
          paragraphs: [
            'When you book an Expertise, you enter into a direct professional services agreement with the Member or Partner. You acknowledge and agree that:'
          ],
          bullets: [
            'You are responsible for verifying the Members\' or Partners\' credentials, qualifications, and suitability for your needs;',
            'Nobilis does not verify, endorse, or guarantee the accuracy of any Expert\'s credentials or the quality of their services;',
            'You should conduct independent due diligence before engaging any Expertise.',
            'The relationship between you and the Expert is independent of Nobilis; and',
            'You assume all risks associated with relying on advice, recommendations, or services provided by Experts'
          ]
        },
        {
          subtitle: '8.4 Professional Services Disclaimer',
          paragraphs: [
            'IMPORTANT: NOBILIS DOES NOT PROVIDE PROFESSIONAL SERVICES AND IS NOT RESPONSIBLE FOR THE PROFESSIONAL SERVICES PROVIDED BY EXPERTS. Expertises facilitated through the Platform do not create an attorney-client, doctor-patient, accountant-client, or any other professional relationship between you and Nobilis. Nobilis does not endorse, recommend, or guarantee any expertise, the advice, opinions, recommendations, or services they provide. Experts are independent contractors and not employees, agents, or representatives of Nobilis.'
          ]
        },
        {
          subtitle: '8.5 Cancellations and Refunds for Expertise',
          paragraphs: [
            'Each Expert sets their own cancellation and refund policy for the Expertise they offer. The Partner\'s specific cancellation and refund terms will be clearly displayed at the time of booking, and you agree to be bound by those terms when you book a consultation.',
            'Nobilis does not set, control, or guarantee any cancellation or refund policies. All cancellation and refund requests must be handled directly between you and the Partner. In the event of a dispute regarding cancellations or refunds, Nobilis may, at its sole discretion, offer to facilitate mediation between the parties, but Nobilis is not obligated to become involved and makes no guarantees regarding dispute outcomes.',
            'You acknowledge and agree that Nobilis is not responsible for any Partner\'s cancellation or refund policies, and any disputes arising from cancellations or refunds are solely between you and the Partner.'
          ]
        }
      ]
    },
    {
      title: '9. MASTERMIND CIRCLES & DISCUSSION FORUMS',
      subsections: [
        {
          subtitle: '9.1 Mastermind Circles',
          paragraphs: [
            'Mastermind Circles are exclusive, invitation-only private forums where select Members collaborate on specific topics, projects, or challenges. Participation in Mastermind Circles is subject to the following:'
          ],
          bullets: [
            'Content posted in Mastermind Circles must comply with these Terms and our Community Guidelines.',
            'Mastermind Circle hosts or facilitators may establish additional rules and guidelines for their Circles.',
            'Mastermind Circles are confidential, and Members must not disclose content, discussions, or participation to anyone outside the Circle without explicit consent from all participants.',
            'Each participant is responsible for any content posted in Mastermind Circles;',
            'Nobilis may monitor Mastermind Circles for compliance with these Terms but is under no obligation to do so; and',
            'Nobilis reserves the right to remove content or ban Members from Mastermind Circles for violations of these Terms.'
          ]
        },
        {
          subtitle: '9.2 Open Discussions',
          paragraphs: [
            'Open Discussions are platform-wide discussion boards accessible to all Members. Open Forums allow Members to engage in broader conversations, share insights, and exchange ideas across the Nobilis community. Participation in Open Forums is subject to the following:'
          ],
          bullets: [
            'Content posted in Open Discussions must comply with these Terms and our Community Guidelines.',
            'While Open Discussions are accessible to all Members, the same confidentiality and privacy protections apply - Members must not screenshot, record, share, or republish Forum content outside the Platform without express consent;',
            'Open discussions should be organized by topic, industry, interest area, or other categories.',
            'Discussion moderators may establish topic-specific guidelines and rules.',
            'You are responsible for any content you post in Open Discussions.',
            'Nobilis may monitor, moderate, edit, or remove content from Open Discussions to maintain community standards and Platform integrity; and',
            'Nobilis reserves the right to remove content, restrict posting privileges, or ban Members from Open Discussions for violations of these Terms.'
          ]
        },
        {
          subtitle: '9.3 General Discussion Forum Rules',
          paragraphs: [
            'Regardless of whether you participate in Mastermind Circles or Open Discussions, all Members agree to:'
          ],
          bullets: [
            'Respect the confidentiality of all discussions and do not share, reproduce, or distribute content outside the Platform;',
            'Engage respectfully, professionally, and constructively with other Members;',
            'Not use forums for commercial solicitation, advertising, or promotional activities outside of Nobilis\'s approved Platform services (Experiences, Expertise);',
            'Not post spam, repetitive content, offensive material, or content that violates intellectual property rights;',
            'Not impersonate others, create fake accounts, or misrepresent your identity or affiliations;',
            'Report violations of these Terms or Community Guidelines to Nobilis support immediately; and',
            'Accept that Nobilis has sole discretion to interpret and enforce forum rules and may take any action deemed necessary to protect the Platform and community.'
          ]
        }
      ]
    },
    {
      title: '10. INTELLECTUAL PROPERTY RIGHTS',
      subsections: [
        {
          subtitle: '10.1 Nobilis Intellectual Property',
          paragraphs: [
            'The Platform and all content, features, and functionality are owned by Nobilis or its licensors and are protected by intellectual property laws. You are granted a limited, non-exclusive, non-transferable, revocable license to access and use the Platform for personal, non-commercial purposes in accordance with these Terms.'
          ]
        },
        {
          subtitle: '10.2 Member Content',
          paragraphs: [
            'You retain ownership of content you submit ("Member Content"). By submitting Member Content, you grant Nobilis a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display such content in connection with operating the Platform. You represent that you have the necessary rights to submit Member Content and that it does not violate any third-party rights.'
          ]
        },
        {
          subtitle: '10.3 Trademarks',
          paragraphs: [
            '"Nobilis" and associated logos are trademarks of Nobilis Global, LLC. You may not use these trademarks without our prior written consent.'
          ]
        }
      ]
    },
    {
      title: '11. PRIVACY AND DATA PROTECTION',
      paragraphs: [
        'Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your information. By using the Platform, you consent to our data practices as described in the Privacy Policy.'
      ]
    },
    {
      title: '12. DISCLAIMERS AND WARRANTIES',
      paragraphs: [
        'THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. NOBILIS DOES NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, OR THAT EXPERIENCES OR EXPERT CONSULTATIONS WILL MEET YOUR EXPECTATIONS. YOU USE THE PLATFORM AT YOUR SOLE RISK.',
        'Nobilis does not guarantee the accuracy of Member-provided information or verification results. Membership does not constitute an endorsement of any Member\'s claimed achievements or net worth.'
      ]
    },
    {
      title: '13. LIMITATION OF LIABILITY',
      paragraphs: [
        'TO THE MAXIMUM EXTENT PERMITTED BY LAW, NOBILIS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THE PLATFORM. NOBILIS\'S TOTAL LIABILITY SHALL NOT EXCEED THE GREATER OF (A) FEES PAID IN THE PRECEDING 12 MONTHS, OR (B) $100.',
        'Some jurisdictions do not allow limitation of liability, so these limitations may not apply to you.'
      ]
    },
    {
      title: '14. INDEMNIFICATION',
      paragraphs: [
        'You agree to indemnify and hold harmless Nobilis from any claims, damages, losses, or expenses arising from: (a) your use of the Platform; (b) violation of these Terms; (c) violation of third-party rights; (d) your Member Content; (e) hosting Experiences or providing Expertise; or (f) participation in Experiences or Expertises hosted by Members and Partners.'
      ]
    },
    {
      title: '15. DISPUTE RESOLUTION BETWEEN MEMBERS AND PARTNERS',
      paragraphs: [
        'If you have a dispute with another Member, you agree to first attempt to resolve it directly. Nobilis may facilitate mediation at its discretion but is not obligated to become involved.',
        'To the maximum extent permitted by law, you release Nobilis from all claims arising from disputes with other Members and Partners.'
      ]
    },
    {
      title: '16. TERM AND TERMINATION',
      subsections: [
        {
          subtitle: '16.1 Term',
          paragraphs: [
            'These Terms commence when you first access the Platform and continue until terminated.'
          ]
        },
        {
          subtitle: '16.2 Termination by You',
          paragraphs: [
            'You may terminate your membership at any time. Termination is effective at the end of your billing period, and you will not receive a refund for unused fees.'
          ]
        },
        {
          subtitle: '16.3 Termination by Nobilis',
          paragraphs: [
            'Nobilis may suspend or terminate your account at any time, with or without cause. Grounds for termination include:'
          ],
          bullets: [
            'Violation of these Terms or applicable laws;',
            'Fraudulent or illegal activity;',
            'Failure to pay fees;',
            'Repeated cancellation of confirmed Experiences you are hosting without force majeure or valid justification;',
            'Conduct that violates Nobilis values, community standards, or undermines the integrity of the Platform;',
            'Harm to Nobilis\'s reputation or the reputation of other Members and Partners;',
            'Repeated complaints from other Members; or',
            'Any other reason at Nobilis\'s sole discretion.'
          ]
        },
        {
          subtitle: '16.4 Effect of Termination',
          paragraphs: [
            'Upon termination, your access ceases immediately. You remain liable for obligations incurred prior to termination. Sections that should survive termination will survive.'
          ]
        }
      ]
    },
    {
      title: '17. DISPUTE RESOLUTION AND ARBITRATION',
      importantNotice: 'PLEASE READ CAREFULLY. THIS AFFECTS YOUR LEGAL RIGHTS.',
      subsections: [
        {
          subtitle: '17.1 Informal Dispute Resolution',
          paragraphs: [
            'Before filing a claim, contact us at legal@joinnobilis.com to attempt informal resolution. If unresolved within 60 days, either party may initiate arbitration.'
          ]
        },
        {
          subtitle: '17.2 Binding Arbitration',
          paragraphs: [
            'Any dispute shall be determined by binding arbitration before a single arbitrator in accordance with American Arbitration Association (AAA) rules. Arbitration will take place in Austin, Texas, unless mutually agreed otherwise. The arbitrator\'s decision is final and binding.'
          ]
        },
        {
          subtitle: '17.3 Class Action Waiver',
          paragraphs: [
            'YOU AND NOBILIS AGREE THAT CLAIMS MAY ONLY BE BROUGHT IN INDIVIDUAL CAPACITY, NOT AS A CLASS, REPRESENTATIVE, OR COLLECTIVE ACTION.'
          ]
        },
        {
          subtitle: '17.4 Exceptions',
          paragraphs: [
            'Either party may bring claims in small claims court or seek injunctive relief for intellectual property violations.'
          ]
        },
        {
          subtitle: '17.5 Opt-Out',
          paragraphs: [
            'You may opt out by sending written notice to 10900 Research Blvd, Suite 160C #1119, Austin, TX 78759 within 30 days of accepting these Terms.'
          ]
        }
      ]
    },
    {
      title: '18. GENERAL PROVISIONS',
      subsections: [
        {
          subtitle: '18.1 Governing Law',
          paragraphs: [
            'These Terms are governed by the laws of the State of Texas, without regard to conflict of law principles.'
          ]
        },
        {
          subtitle: '18.2 Entire Agreement',
          paragraphs: [
            'These Terms, together with the Privacy Policy, constitute the entire agreement between you and Nobilis.'
          ]
        },
        {
          subtitle: '18.3 Amendment',
          paragraphs: [
            'Nobilis may modify these Terms at any time. Continued use after notice constitutes acceptance.'
          ]
        },
        {
          subtitle: '18.4 Waiver',
          paragraphs: [
            'No waiver of any provision shall be deemed a continuing waiver.'
          ]
        },
        {
          subtitle: '18.5 Severability',
          paragraphs: [
            'If any provision is invalid, the remaining provisions remain in full force.'
          ]
        },
        {
          subtitle: '18.6 Assignment',
          paragraphs: [
            'You may not assign these Terms without Nobilis\'s consent. Nobilis may assign without restriction.'
          ]
        },
        {
          subtitle: '18.7 Force Majeure',
          paragraphs: [
            'Nobilis is not liable for failures due to circumstances beyond reasonable control.'
          ]
        },
        {
          subtitle: '18.8 Contact Information',
          paragraphs: [
            'For questions about these Terms, contact us at:',
            'Nobilis Global, LLC',
            '10900 Research Blvd, Suite 160C #1119, Austin, TX 78759',
            'Email: legal@joinnobilis.com',
            'Phone: +1 (737) 471-4240'
          ]
        }
      ]
    }
  ],
  closingStatement: 'By using the Nobilis Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.'
}